import { useCallback, useMemo, useState } from 'react';

import moment from 'moment';

import { useNotification } from '@sine/sine-ui-components';

import { QueryKeys, queryClient } from '../../../query.client';
import type { LineItem, ManageTableRecord, PlanSelectOption } from '../../../services/billing/billing.types';
import { LICENSE_TYPE } from '../../../services/billing/billing.types';
import { useBilling } from '../BillingProvider';
import {
  buildLicenceRecords,
  isManagedSubscription,
  isTrial,
  mapLicenseTypeToSelectOption,
  mapSelectOptionToLicenseType,
} from '../billing.helpers';
import { newLicenceTypeOptions } from '../constants/billing.constants';
import { useCreateManualLicence, useCreateManualSubscription, useUpdateManualSubscription } from './billing.hooks';
import { useLineItemManagement } from './useLineItemManagement.hook';

export interface UseManualManageModalParams {
  onClose: (refresh?: boolean) => void;
  siteId?: string;
  /** Fallback site object for newly created sites not yet in billing data */
  site?: { id: string; name: string };
}

function mapRecordToValidLineItem(record: ManageTableRecord): LineItem {
  const { licence, site } = record;

  if (!licence) {
    return {
      site,
      selectedPlanOption: newLicenceTypeOptions[0],
      licenceType: 'N/A',
      startDate: 'N/A',
      endDate: 'N/A',
      note: '',
    };
  }

  if (isTrial(licence.licenceType)) {
    return {
      id: licence.id,
      type: licence.type,
      licenceType: licence.licenceType,
      site,
      selectedPlanOption: newLicenceTypeOptions[0],
      endDate: licence.expiry,
      startDate: licence.startDate,
      note: licence.note || '',
    };
  }

  const licenseMapping = mapLicenseTypeToSelectOption(licence.licenceType);
  const selectedOption =
    newLicenceTypeOptions.find((opt) => opt.value === licenseMapping?.vmsPlanType) || newLicenceTypeOptions[0];

  return {
    id: licence.id,
    type: licence.type,
    licenceType: licence.licenceType,
    site,
    selectedPlanOption: selectedOption,
    endDate: licence.expiry,
    startDate: licence.startDate,
    note: licence.note || '',
  };
}

export function useManualManageModal({ onClose, siteId, site }: UseManualManageModalParams) {
  const { addNotification } = useNotification();
  const { subscriptions, sites, trials, unlicencedSites, currentTeam } = useBilling();

  // Get managed subscription - may be undefined if no managed subscription exists
  const managedSubscriptions = useMemo(
    () => subscriptions.filter(isManagedSubscription).filter((s) => s.deleted === null),
    [subscriptions],
  );
  const subscription = managedSubscriptions.at(0);

  // State management
  const [startDate, setStartDate] = useState<Date>(
    subscription?.valid_from ? new Date(subscription.valid_from) : new Date(),
  );
  const [endDate, setEndDate] = useState<Date>(
    subscription?.valid_to ? new Date(subscription.valid_to) : moment().add(1, 'year').subtract(1, 'day').toDate(),
  );

  // Invalidate queries after successful mutation
  const invalidateBillingQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [QueryKeys.SUBSCRIPTIONS, currentTeam.id] }),
      queryClient.invalidateQueries({ queryKey: [QueryKeys.BILLING_SITES, currentTeam.id] }),
    ]);
  }, [currentTeam.id]);

  // Use mutation hooks from billing.hooks
  const { updateManualSubscription, isUpdatingManualSubscription } = useUpdateManualSubscription(currentTeam.id);
  const { createManualLicence, isCreatingManualLicence } = useCreateManualLicence(currentTeam.id);
  const { createManualSubscription, isCreatingManualSubscription } = useCreateManualSubscription(currentTeam.id);

  const isLoading = isUpdatingManualSubscription || isCreatingManualLicence || isCreatingManualSubscription;

  // Single site mode flag
  const isSingleSiteMode = !!siteId;

  // Compute ALL licenses from the subscription (needed for updates to preserve other licenses)
  const allLicenses = useMemo(
    () =>
      buildLicenceRecords({
        sites,
        trials,
        subscription,
        unlicencedSites,
        includeTrials: true,
        includeOngoingLicences: true,
        includeUnlicensedSites: true,
      })
        .filter(({ licence }) => !licence?.deleted && !licence?.metadata?.cancel_at_period_end)
        .filter(({ licence, site: recordSite }) => !(isTrial(licence?.licenceType) && !recordSite))
        .map((record) => mapRecordToValidLineItem(record)),
    [sites, trials, subscription, unlicencedSites],
  );

  // Compute displayed licenses (filtered by siteId if provided)
  const initialLicenses = useMemo(() => {
    if (!siteId) {
      return allLicenses;
    }

    // First, try to find the site in existing license records
    const matchingLicenses = allLicenses.filter((item) => item.site?.id === siteId);
    if (matchingLicenses.length > 0) {
      return matchingLicenses;
    }

    // If not found, look for the site in sites or unlicencedSites and create an empty record
    const siteFromBilling = sites.find((s) => s.id === siteId) || unlicencedSites.find((s) => s.id === siteId);
    if (siteFromBilling) {
      return [
        {
          site: siteFromBilling,
          selectedPlanOption: newLicenceTypeOptions[0],
          licenceType: 'N/A',
          startDate: 'N/A',
          endDate: 'N/A',
          note: '',
        },
      ];
    }

    // Use the provided site prop as a fallback (for newly created sites not yet in billing data)
    if (site) {
      return [
        {
          site: site as LineItem['site'],
          selectedPlanOption: newLicenceTypeOptions[0],
          licenceType: 'N/A',
          startDate: 'N/A',
          endDate: 'N/A',
          note: '',
        },
      ];
    }

    // Site not found anywhere - return empty array (will show no items)
    return [];
  }, [allLicenses, siteId, sites, unlicencedSites, site]);

  // Line item management
  const { lineItems, addLineItem, removeLineItem, updateLineItem } = useLineItemManagement({
    initialItems: initialLicenses,
    defaultPlanOption: newLicenceTypeOptions[0],
    planOptions: newLicenceTypeOptions,
    currency: 'aud',
  });

  // Handler: update note on line item
  const handleNoteChange = useCallback(
    (lineItem: LineItem, note: string) => {
      const updatedLineItem = { ...lineItem, note };
      updateLineItem(updatedLineItem, lineItem.selectedPlanOption);
    },
    [updateLineItem],
  );

  // Handler: create or update manual license
  const createOrUpdateManualLicense = useCallback(async () => {
    const formattedStartDate = moment(startDate).format();
    const formattedEndDate = moment(endDate).format();

    // Map displayed line items to the format needed for API calls
    const displayedItems = lineItems.map((item) => {
      const licenseType = item.selectedPlanOption?.type || item.licenceType;
      const licenseMapping = licenseType ? mapLicenseTypeToSelectOption(licenseType) : null;

      const planType = licenseMapping
        ? mapSelectOptionToLicenseType(
            licenseMapping.vmsPlanType,
            licenseMapping.addContractors,
            licenseMapping.cmsPlanType,
          )
        : LICENSE_TYPE.VMSLITE;

      return {
        id: item.id || null,
        type: planType,
        note: item.note || '',
        siteId: item.site && !('isNew' in item.site && item.site.isNew) ? item.site.id : undefined,
      };
    });

    const firstCmsItem = displayedItems.find((item) => {
      const mapping = mapLicenseTypeToSelectOption(item.type);
      return mapping?.cmsPlanType === 'cmslite' || mapping?.cmsPlanType === 'cmsplus';
    });

    const cmsPlanToPass = firstCmsItem ? mapLicenseTypeToSelectOption(firstCmsItem.type)?.cmsPlanType : undefined;

    try {
      if (isSingleSiteMode && subscription) {
        // SINGLE SITE MODE: Only add/update the specific site's license
        const displayedExisting = displayedItems.filter((item) => item.id);
        const displayedNew = displayedItems.filter((item) => !item.id);

        if (displayedNew.length > 0) {
          // Site doesn't have a license yet - add it to the existing subscription
          await createManualLicence({
            subscriptionId: subscription.id,
            licences: displayedNew.map(({ siteId: itemSiteId, type, note }) => ({
              siteId: itemSiteId,
              plan: type,
              note,
            })),
          });
          addNotification({
            heading: 'License added successfully',
          });
        } else if (displayedExisting.length > 0) {
          // Site already has a license - merge with all other existing licenses and update
          const otherExistingLicenses = allLicenses
            .filter((item) => item.id && item.site?.id !== siteId)
            .map((item) => {
              const licenseType = item.selectedPlanOption?.type || item.licenceType;
              const licenseMapping = licenseType ? mapLicenseTypeToSelectOption(licenseType) : null;
              const planType = licenseMapping
                ? mapSelectOptionToLicenseType(
                    licenseMapping.vmsPlanType,
                    licenseMapping.addContractors,
                    licenseMapping.cmsPlanType,
                  )
                : LICENSE_TYPE.VMSLITE;
              return {
                id: item.id!,
                type: planType,
                note: item.note || '',
              };
            });

          const allExistingLicenses = [
            ...otherExistingLicenses,
            ...displayedExisting.map(({ id, type, note }) => ({ id: id, type, note })),
          ];

          await updateManualSubscription({
            subscriptionId: subscription.id,
            startDate: formattedStartDate || subscription.valid_from,
            endDate: formattedEndDate || subscription.valid_to,
            items: allExistingLicenses,
          });
          addNotification({
            heading: 'License updated successfully',
          });
        }
      } else if (subscription) {
        // MULTI-SITE MODE: Update all licenses
        const existingLicenses = displayedItems.filter((item) => item.id);
        const newLicenses = displayedItems.filter((item) => !item.id);

        // Update existing subscription
        await updateManualSubscription({
          subscriptionId: subscription.id,
          startDate: formattedStartDate || subscription.valid_from,
          endDate: formattedEndDate || subscription.valid_to,
          items: existingLicenses.map(({ id, type, note }) => ({ id, type, note })),
        });

        if (newLicenses.length > 0 && existingLicenses.length > 0) {
          // Add new licences to existing subscription
          await createManualLicence({
            subscriptionId: subscription.id,
            licences: newLicenses.map(({ siteId: itemSiteId, type, note }) => ({
              siteId: itemSiteId,
              plan: type,
              note,
            })),
          });
        } else if (newLicenses.length > 0 && existingLicenses.length === 0) {
          // Create new subscription for new licences only
          await createManualSubscription({
            convertToStripe: false,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            licences: newLicenses.map(({ siteId: itemSiteId, type, note }) => ({
              siteId: itemSiteId,
              plan: type,
              note,
            })),
            cmsPlan: cmsPlanToPass,
          });
        }

        addNotification({
          heading: 'Manual subscription updated successfully',
        });
      } else {
        // NO SUBSCRIPTION: Create new subscription
        await createManualSubscription({
          convertToStripe: false,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          licences: displayedItems.map(({ siteId: itemSiteId, type, note }) => ({
            siteId: itemSiteId,
            plan: type,
            note,
          })),
          cmsPlan: cmsPlanToPass,
        });
        addNotification({
          heading: 'Manual subscription created successfully',
        });
      }

      await invalidateBillingQueries();
      onClose(true);
    } catch {
      // Error handling is done in mutation onError callbacks
    }
  }, [
    startDate,
    endDate,
    lineItems,
    subscription,
    isSingleSiteMode,
    siteId,
    allLicenses,
    addNotification,
    updateManualSubscription,
    createManualLicence,
    createManualSubscription,
    invalidateBillingQueries,
    onClose,
  ]);

  // Handler: update plan on line item
  const handlePlanChange = useCallback(
    (lineItem: LineItem, planOption: PlanSelectOption | undefined) => {
      updateLineItem(lineItem, planOption);
    },
    [updateLineItem],
  );

  return {
    state: {
      isLoading,
      startDate,
      endDate,
      hasSubscription: !!subscription,
    },
    values: {
      lineItems,
      planOptions: newLicenceTypeOptions,
      subscription,
    },
    handlers: {
      setStartDate,
      setEndDate,
      addLineItem,
      removeLineItem,
      updateLineItem: handlePlanChange,
      handleNoteChange,
      createOrUpdateManualLicense,
    },
  };
}
